import jsPDF from "jspdf"
import "jspdf-autotable"
import { deliverPdf } from "./helpers/pdfDelivery.js"
import { drawPdfLogo } from "./helpers/pdfLogo.js"

const formatCurrency = (value) => {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(value)
}

const formatDate = (dateString) => {
  if (!dateString) return "N/A"
  const dateObj = new Date(dateString)
  if (isNaN(dateObj.getTime())) return String(dateString)
  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(dateObj)
}


export const generarPdfTablaGastos = ({ 
  gastos, 
  filtros, 
  totalMontoGastos, 
  logoBase64 = null,
  deliveryContext = null,
}) => {
  // Configuración del documento en formato horizontal (landscape)
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" })
  
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 20 // Reducido de 30 a 20 para aprovechar más espacio

  // Paleta de colores
  const PAL = {
    primary: [25, 118, 210],
    secondary: [120, 144, 156],
    ink: [33, 33, 33],
    muted: [117, 117, 117],
    inkLight: [158, 158, 158],
    bgSoft: [245, 247, 250],
    success: [76, 175, 80],
    error: [244, 67, 54],
  }

  const HEADER = {
    logoX: 30,
    logoY: 12,
    logoMaxW: 80,
    logoMaxH: 36,
    titleY: 36,
    metaY: 54,
    contentStartY: 74,
  }

  drawPdfLogo({
    doc,
    logoBase64,
    x: HEADER.logoX,
    y: HEADER.logoY,
    maxW: HEADER.logoMaxW,
    maxH: HEADER.logoMaxH,
  })

  doc.setFont("helvetica", "bold")
  doc.setFontSize(16)
  doc.setTextColor(...PAL.primary)
  doc.text("REPORTE DE GASTOS", pageWidth / 2, HEADER.titleY, { align: "center" })

  doc.setFont("helvetica", "normal")
  doc.setFontSize(9)
  doc.setTextColor(...PAL.inkLight)
  doc.text(`Fecha de generación: ${formatDate(new Date())}`, pageWidth - margin, HEADER.metaY, { align: "right" })

  let yPosition = HEADER.contentStartY

  // Sección de filtros aplicados
  doc.setFont("helvetica", "bold")
  doc.setFontSize(11)
  doc.setTextColor(...PAL.ink)
  doc.text("Filtros aplicados:", margin, yPosition)
  yPosition += 15

  // Línea separadora
  doc.setDrawColor(...PAL.primary)
  doc.setLineWidth(1)
  doc.line(margin, yPosition, pageWidth - margin, yPosition)
  yPosition += 12

  // Mostrar filtros
  doc.setFont("helvetica", "normal")
  doc.setFontSize(9)
  doc.setTextColor(...PAL.muted)
  
  let filtrosTexto = []
  
  if (filtros.categoria) {
    filtrosTexto.push(`Categoría: ${filtros.categoria.descripcion || filtros.categoria.label || "N/A"}`)
  }
  
  if (filtros.subcategoria) {
    filtrosTexto.push(`Subcategoría: ${filtros.subcategoria.descripcion || filtros.subcategoria.label || "N/A"}`)
  }
  
  if (filtros.entidad) {
    filtrosTexto.push(`Proveedor: ${filtros.entidad.descripcion || filtros.entidad.label || "N/A"}`)
  }
  
  if (filtros.startDate || filtros.endDate) {
    const inicio = filtros.startDate ? formatDate(filtros.startDate) : "N/A"
    const fin = filtros.endDate ? formatDate(filtros.endDate) : "N/A"
    filtrosTexto.push(`Rango: ${inicio} - ${fin}`)
  }

  if (filtrosTexto.length === 0) {
    filtrosTexto.push("Sin filtros aplicados")
  }

  const filtrosLinea = filtrosTexto.join(" | ")
  doc.text(filtrosLinea, margin, yPosition)
  yPosition += 20

  // Total de gastos destacado
  doc.setFont("helvetica", "bold")
  doc.setFontSize(12)
  doc.setTextColor(...PAL.error)
  const totalTexto = `TOTAL DE GASTOS: ${formatCurrency(totalMontoGastos)}`
  const totalWidth = doc.getTextWidth(totalTexto)
  const totalX = (pageWidth - totalWidth) / 2
  
  // Fondo para el total
  doc.setFillColor(...PAL.bgSoft)
  doc.roundedRect(totalX - 10, yPosition - 12, totalWidth + 20, 20, 3, 3, "F")
  doc.text(totalTexto, totalX, yPosition)
  yPosition += 30

  // Preparar datos para la tabla
  const gastosData = gastos.map((g) => [
    g.id.toString(),
    g.fecha,
    formatCurrency(g.montoTotal),
    g.proveedor,
    g.descripcion,
    g.categoria,
    g.subcategoria,
  ])

  // Calcular ancho total de la tabla y centrarla
  const tableWidth = 45 + 80 + 80 + 120 + 200 + 110 + 110 // 745pt
  const marginLeft = (pageWidth - tableWidth) / 2 // Centrar la tabla

  // Generar tabla con autoTable
  doc.autoTable({
    head: [['ID', 'Fecha', 'Monto', 'Proveedor', 'Descripción', 'Categoría', 'Subcategoría']],
    body: gastosData,
    startY: yPosition,
    theme: 'grid',
    headStyles: {
      fillColor: PAL.primary,
      fontSize: 9,
      fontStyle: 'bold',
      halign: 'center',
      textColor: [255, 255, 255],
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 4,
      textColor: PAL.ink,
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250],
    },
    columnStyles: {
      0: { cellWidth: 45, halign: 'center' }, // ID
      1: { cellWidth: 80, halign: 'center' }, // Fecha
      2: { cellWidth: 80, halign: 'right' }, // Monto
      3: { cellWidth: 120, overflow: 'linebreak', valign: 'middle' }, // Proveedor
      4: { cellWidth: 200, overflow: 'linebreak', valign: 'middle' }, // Descripción (más ancho)
      5: { cellWidth: 110, overflow: 'linebreak', valign: 'middle' }, // Categoría
      6: { cellWidth: 110, overflow: 'linebreak', valign: 'middle' }, // Subcategoría
    },
    margin: { left: marginLeft, right: marginLeft }, // Tabla centrada horizontalmente
    didDrawPage: (data) => {
      // Pie de página con número de página
      doc.setFont("helvetica", "normal")
      doc.setFontSize(8)
      doc.setTextColor(...PAL.inkLight)
      const pageText = `Página ${data.pageNumber} de ${doc.internal.pages.length - 1}`
      doc.text(pageText, pageWidth / 2, pageHeight - 20, { align: "center" })
    },
  })

  // Agregar total de registros al final
  const finalY = doc.lastAutoTable.finalY + 15
  doc.setFont("helvetica", "bold")
  doc.setFontSize(10)
  doc.setTextColor(...PAL.ink)
  doc.text(`Total de registros: ${gastos.length}`, margin, finalY)

  // Descargar el PDF
  const fileName = `gastos_${new Date().toISOString().split("T")[0]}.pdf`
  doc.setProperties({
    title: fileName.replace(/\.pdf$/i, ""),
    subject: fileName.replace(/\.pdf$/i, ""),
    author: "LUTENTE TOURS",
    creator: "LUTENTE TOURS",
  })
  deliverPdf({ doc, fileName, targetWindow: deliveryContext?.targetWindow || null })
}
